import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowUpRight, Check, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';

const LEVEL_LABELS = {
  starting_out: 'Starting Out',
  beginner: 'Beginner',
  intermediate: 'Intermediate',
  advanced: 'Advanced',
};

export const LevelUpPopup = () => {
  const { api } = useAuth();
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(false);
  const [pending, setPending] = useState(false);
  const [confirmation, setConfirmation] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get('/user/progression');
        if (cancelled) return;
        if (res.data?.promotion?.status === 'suggested') {
          setData(res.data);
          setOpen(true);
        }
      } catch (e) {
        // progression is non-critical for the dashboard
      }
    })();
    return () => { cancelled = true; };
  }, [api]);

  const respond = async (action) => {
    setPending(true);
    try {
      const res = await api.post('/user/promotion/respond', { action });
      if (action === 'accept') {
        setConfirmation(res.data?.coach_message || 'Your level has been updated.');
      } else {
        toast.success(res.data?.coach_message || 'No change — we\'ll revisit later.');
        setOpen(false);
      }
    } catch (e) {
      toast.error('Something went wrong. Please try again.');
    } finally {
      setPending(false);
    }
  };

  if (!data) return null;
  const suggested = data.promotion?.suggested_level;
  const label = LEVEL_LABELS[suggested] || suggested;

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          className="fixed inset-0 z-[70] flex items-center justify-center p-5 bg-black/85 backdrop-blur-sm"
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          data-testid="levelup-popup"
        >
          <motion.div
            className="w-full max-w-sm rounded-3xl border-2 border-accent/40 bg-[#0B0B0B] overflow-hidden"
            style={{ boxShadow: '0 24px 70px rgba(0,0,0,0.7), inset 0 1px 0 rgba(var(--accent-rgb),0.15)' }}
            initial={{ scale: 0.92, y: 20 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.95, opacity: 0 }}
            transition={{ type: 'spring', damping: 22, stiffness: 260 }}
          >
            <div className="px-6 pt-7 pb-6 text-center">
              <div className="w-14 h-14 mx-auto rounded-2xl border-2 border-accent/40 bg-accent/10 flex items-center justify-center mb-4">
                {confirmation
                  ? <Check size={26} className="text-accent" strokeWidth={3} />
                  : <ArrowUpRight size={26} className="text-accent" strokeWidth={3} />}
              </div>

              {confirmation ? (
                <>
                  <p className="text-xs font-heading font-black uppercase tracking-[0.2em] text-accent mb-2">Level updated</p>
                  <h2 className="text-2xl font-heading font-black uppercase text-white leading-tight" data-testid="levelup-confirm-title">
                    Welcome to {label}
                  </h2>
                  <p className="text-sm text-white/60 mt-3 leading-relaxed" data-testid="levelup-coach-message">
                    {confirmation}
                  </p>
                  <Button
                    onClick={() => window.location.reload()}
                    className="mt-6 w-full py-3.5 bg-accent text-accent-foreground hover:bg-accent/90 rounded-2xl font-heading font-black uppercase tracking-wide text-sm active:scale-[0.98] transition-transform"
                    data-testid="levelup-done-btn"
                  >
                    See my program
                  </Button>
                </>
              ) : (
                <>
                  <p className="text-xs font-heading font-black uppercase tracking-[0.2em] text-accent mb-2">Level unlocked</p>
                  <h2 className="text-2xl font-heading font-black uppercase text-white leading-tight" data-testid="levelup-title">
                    You're ready to move up to {label}
                  </h2>
                  <p className="text-sm text-white/60 mt-3 leading-relaxed">
                    Moving up rebuilds your upcoming program around your goals, equipment and schedule. Your completed history stays saved.
                  </p>

                  <div className="mt-6 space-y-2.5">
                    <Button
                      onClick={() => respond('accept')}
                      disabled={pending}
                      className="w-full py-3.5 bg-accent text-accent-foreground hover:bg-accent/90 rounded-2xl gap-2 font-heading font-black uppercase tracking-wide text-sm active:scale-[0.98] transition-transform disabled:opacity-70"
                      data-testid="levelup-accept-btn"
                    >
                      {pending
                        ? <><Loader2 size={17} className="animate-spin" /> Rebuilding your program…</>
                        : <>Move up</>}
                    </Button>
                    <Button
                      onClick={() => respond('decline')}
                      disabled={pending}
                      variant="ghost"
                      className="w-full h-11 rounded-2xl text-white/50 hover:text-white/80 font-heading font-bold uppercase tracking-wide text-xs"
                      data-testid="levelup-decline-btn"
                    >
                      Not now
                    </Button>
                  </div>
                </>
              )}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default LevelUpPopup;
